import { Button, Dialog, Icon, Typography, Progress } from '@equinor/eds-core-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { chevron_left, chevron_right, close, error_outlined } from '@equinor/eds-icons'
import { DeviceDto, InstalledSensorStatus, PlantDto } from '@/core/models/types'
import { COORDINATES_REGEX, useOnboardingStepper } from '../../hooks/useOnboardingStepper'
import { useOnboardingStepContent } from '../../hooks/useOnboardingStepContent'
import { useAtom } from 'jotai'
import { imagesAtom, descriptionsAtom, showErrorSelectionBarAtom, existingImagesAtom } from '@/core/store/globalStore'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import Form from './Form'
import UploadImage from './UploadImage'
import Overview from './Overview'
import OnboardSelectionBar from './OnboardSelectionBar'
import {
    baseFont,
    baseLinkStyles,
    EdsStyledActionLabel,
    EdsStyledLabel,
    StyledErrorTypography,
    StyledWarningTypography,
    SummaryErrorContainer,
    SummaryWarningContainer,
} from '@/styles/layout'
import { Sensor } from '../../models'

interface OnboardingStepperProps {
    showMenu: boolean
    onClose: () => void
    onReopen?: () => void
    onOpenDraft?: (serialNumber: string, plantShortName?: string) => void
    onOpenCreatedSensor?: (serialNumber: string, plantShortName?: string) => void
    plants: PlantDto[]
    devices: DeviceDto[]
    sensorToEdit: Sensor | null
}
export const missingSuperiorTag = 'Missing superior tag. You can find the superior tag in STID.'
export const missingCoordinates = 'Missing coordinates. You can find the coordinates in Echo.'
export const missingVendorSerialNumber =
    'Please enter the vendor serial number. You can find the number on the sensor or by scanning the QR code.'
export const missingArea = 'Missing area.'
export const missingSelectionBarFields =
    'Please fill in all mandatory fields before continuing. These fields cannot be left empty.'
export const missingVendorSerialNumberBeforeContinuing =
    'Please fill in vendor serial number before continuing. This field cannot be left empty.'
export const invalidCoordinatesFormat = 'Some fields contain incorrect format. Please check coordinates.'
export const incompleteInstallationFields =
    'All fields must be filled out to complete the installation of the sensor. Press “Back” to continue or save to draft.'

const OnboardingStepper: React.FC<OnboardingStepperProps> = ({
    showMenu,
    onClose,
    onReopen,
    onOpenDraft,
    onOpenCreatedSensor,
    plants,
    devices,
    sensorToEdit,
}) => {
    const {
        handleNext,
        handleBack,
        activeStep,
        isLoading,
        isDraftLoading,
        isOnboardSuccess,
        showErrorOverview,
        draftSuccessSerialNumber,
        draftError,
        createdSensorSerialNumber,
        draftAvailable,
        handleFinishProcessClick,
        handleSaveToDraftClick,
        handleCloseDraftSuccess,
        handleGoToDraft,
        handleAddAnotherSensor,
        handleGoToCreatedSensor,
        handleCloseSensorSuccess,
        handleAddAnotherSensorFromCreated,
        handleRetryDraft,
        isNavigatingToDraft,
        isNavigatingToCreatedSensor,
        draftQueuedOffline,
        isOnboardQueuedOffline,
    } = useOnboardingStepper({ onClose, onReopen, onOpenDraft, onOpenCreatedSensor })

    const {
        handleScan,
        handleSelectionChange,
        handleClear,
        handleFormUpdate,
        handleUpdateExistingImages,
        resolveDeviceFromVendorSerialNumber,
        selectedPlant,
        selectedDevice,
        selectedProject,
        inputValues,
        sensorData,
        plantList,
        deviceList,
        projectList,
        areaList,
        isLoadingSensorImages,
    } = useOnboardingStepContent({ showMenu, plants, devices, sensorToEdit })

    const [, setImages] = useAtom(imagesAtom)
    const [, setDescriptions] = useAtom(descriptionsAtom)
    const [, setExistingImages] = useAtom(existingImagesAtom)
    const [showErrorSelectionBar] = useAtom(showErrorSelectionBarAtom)
    const [scannerFetchingData, setScannerFetchingData] = useState(false)
    const windowWidth = useWindowWidth()

    const steps = [{ label: 'Scan' }, { label: 'Details' }, { label: 'Upload images' }, { label: 'Confirm' }]

    const firstStepComplete = () => {
        const isAvailable = selectedPlant && selectedDevice && selectedProject && inputValues.VendorSerialNumber
        return Boolean(isAvailable)
    }

    const isRunning = () => {
        return scannerFetchingData || isLoading || isDraftLoading || isLoadingSensorImages
    }

    useEffect(() => {
        if (!showMenu) {
            setImages([])
            setDescriptions([])
            setExistingImages([])
        }
    }, [showMenu])

    const isCoordinatesEmpty = inputValues.Coordinates === ''
    const isCoordinatesValid = COORDINATES_REGEX.test(inputValues.Coordinates)

    const getStepValidationMessage = (): { severity: 'error' | 'warning'; text: string } | null => {
        if (activeStep === 0 && showErrorSelectionBar && !inputValues.VendorSerialNumber) {
            return { severity: 'error', text: missingVendorSerialNumberBeforeContinuing }
        }
        if (activeStep === 0 && showErrorSelectionBar && !firstStepComplete()) {
            return { severity: 'error', text: missingSelectionBarFields }
        }
        if (activeStep === 1 && !isCoordinatesEmpty && !isCoordinatesValid) {
            return { severity: 'error', text: invalidCoordinatesFormat }
        }
        if (activeStep === 2 && showErrorOverview && (!inputValues.SuperiorTag || !inputValues.Coordinates)) {
            return { severity: 'warning', text: incompleteInstallationFields }
        }
        return null
    }

    const stepValidationMessage = getStepValidationMessage()

    const isMobile = windowWidth <= 1024
    const isLastStep = activeStep === steps.length - 1
    // An unsuccessful attempt may already own a STID tag/timeseries; saving it as draft would orphan them.
    const canSaveToDraft = sensorToEdit?.status !== InstalledSensorStatus.Unsuccessful
    // On the last step, mobile also disables Back while a draft/create request is in flight.
    const backButtonDisabled = activeStep === 0 || (isLastStep && isMobile ? isRunning() : scannerFetchingData)

    const renderBackButton = () => (
        <NavigateBackButton as="Link" onClick={handleBack} disabled={backButtonDisabled}>
            {activeStep !== 0 && (
                <>
                    <StyledIcon data={chevron_left} size={16} />
                    Back
                </>
            )}
        </NavigateBackButton>
    )

    const renderNextButton = () => (
        <NavigateNextButton type="submit" onClick={handleNext} disabled={scannerFetchingData}>
            <EdsStyledActionLabel>Next</EdsStyledActionLabel>
            <Icon data={chevron_right} size={16} />
        </NavigateNextButton>
    )

    const renderLastStepActions = () => (
        <div style={{ display: 'flex', gap: '16px', marginLeft: 'auto' }}>
            {canSaveToDraft && (
                <StepperStyledLinkButton
                    onClick={handleSaveToDraftClick}
                    disabled={isRunning() || (isMobile && !draftAvailable())}
                >
                    {isDraftLoading ? <Progress.Dots /> : <Label>{isMobile ? 'Save as draft' : 'Save to draft'}</Label>}
                </StepperStyledLinkButton>
            )}
            <StepperStyledButton onClick={handleFinishProcessClick} disabled={isRunning()}>
                {isLoading ? <Progress.Dots /> : <StepperStyledActionLabel>Create</StepperStyledActionLabel>}
            </StepperStyledButton>
        </div>
    )

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <>
                        <OnboardSelectionBar
                            plants={plantList}
                            devices={deviceList}
                            projects={projectList}
                            selectedPlant={selectedPlant}
                            selectedDevice={selectedDevice}
                            selectedProject={selectedProject}
                            onSelectionChange={handleSelectionChange}
                            onClear={handleClear}
                            vendorSerialNumber={inputValues.VendorSerialNumber}
                            onVendorSerialNumberChange={(value) => handleFormUpdate({ VendorSerialNumber: value })}
                            onVendorSerialNumberCommit={resolveDeviceFromVendorSerialNumber}
                            onScan={handleScan}
                            setScannerFetchingData={setScannerFetchingData}
                        />
                    </>
                )
            case 1:
                return (
                    <Form
                        areas={areaList}
                        selectedPlant={selectedPlant}
                        selectedDevice={selectedDevice}
                        selectedProject={selectedProject}
                        onSelectionChange={handleSelectionChange}
                        onClear={handleClear}
                        onUpdate={handleFormUpdate}
                        currentSensorData={inputValues}
                        sensorData={sensorData}
                    />
                )
            case 2:
                return (
                    <>
                        <OverviewContainer>
                            <Overview
                                sensorData={sensorData}
                                selectedPlant={selectedPlant}
                                selectedDevice={selectedDevice}
                                selectedProject={selectedProject}
                                currentSensorData={inputValues}
                                onUpdate={handleFormUpdate}
                                displayAdditionalDescription={false}
                                displaySensorData={true}
                                displayImageNumber={false}
                            />
                        </OverviewContainer>
                        <UploadImage
                            existingImages={inputValues.ExistingImages}
                            onUpdateExistingImages={handleUpdateExistingImages}
                        />
                    </>
                )
            case 3:
                return (
                    <OverviewContainer>
                        <Overview
                            sensorData={sensorData}
                            selectedPlant={selectedPlant}
                            selectedDevice={selectedDevice}
                            selectedProject={selectedProject}
                            currentSensorData={inputValues}
                            onUpdate={handleFormUpdate}
                            displayAdditionalDescription={true}
                            displaySensorData={true}
                            displayImageNumber={true}
                        />
                    </OverviewContainer>
                )
            default:
                return null
        }
    }

    if (!showMenu && !draftSuccessSerialNumber && !createdSensorSerialNumber) return null

    return (
        <>
            {showMenu && !draftSuccessSerialNumber && !createdSensorSerialNumber && (
                <OnboardingStepperContainer open={showMenu} onClose={onClose}>
                    <MenuHeader>
                        <EdsStyledLabel group="heading" variant={isMobile ? 'h4' : 'h5'}>
                            Add new sensor
                        </EdsStyledLabel>
                        {isMobile ? (
                            <CloseButton as="Link" onClick={onClose}>
                                Close
                                <StyledIcon data={close} size={24} />
                            </CloseButton>
                        ) : (
                            <Button
                                style={{ height: '32px', width: '32px', margin: '16px' }}
                                variant="ghost_icon"
                                aria-label="Close popover"
                                onClick={onClose}
                            >
                                <Icon data={close} size={24} />
                            </Button>
                        )}
                    </MenuHeader>
                    <StepContent>{renderStepContent()}</StepContent>
                    {isLastStep ? (
                        <ButtonContainer>
                            <RowContainerBottom>
                                {renderBackButton()}
                                {renderLastStepActions()}
                            </RowContainerBottom>
                        </ButtonContainer>
                    ) : (
                        <RowContainerBottom>
                            {renderBackButton()}
                            {renderNextButton()}
                        </RowContainerBottom>
                    )}

                    {stepValidationMessage &&
                        (stepValidationMessage.severity === 'error' ? (
                            <SummaryErrorContainer>
                                <Icon data={error_outlined} title="Error" size={16} />
                                <StyledErrorTypography>{stepValidationMessage.text}</StyledErrorTypography>
                            </SummaryErrorContainer>
                        ) : (
                            <SummaryWarningContainer>
                                <Icon data={error_outlined} title="Warning" size={16} />
                                <StyledWarningTypography>{stepValidationMessage.text}</StyledWarningTypography>
                            </SummaryWarningContainer>
                        ))}
                </OnboardingStepperContainer>
            )}

            {draftSuccessSerialNumber && (
                <DraftDialog open={!!draftSuccessSerialNumber} onClose={handleCloseDraftSuccess}>
                    <DraftContent tabIndex={-1}>
                        <DraftHeader>
                            <DraftTitle>
                                {draftError
                                    ? 'Error saving draft'
                                    : draftQueuedOffline
                                      ? 'Draft saved offline'
                                      : 'Draft created'}
                            </DraftTitle>
                            <CloseButton
                                as="Link"
                                aria-label="Close dialog"
                                variant="ghost_icon"
                                onClick={handleCloseDraftSuccess}
                            >
                                <MobileLabel>Close</MobileLabel>
                                <StyledIcon data={close} size={24} />
                            </CloseButton>
                        </DraftHeader>
                        <DraftBody>
                            {draftError ? (
                                <DraftErrorMessage>
                                    <Icon data={error_outlined} size={18} color="#EB0000" />
                                    Failed to save draft for serial number {draftSuccessSerialNumber}. {draftError}
                                </DraftErrorMessage>
                            ) : draftQueuedOffline ? (
                                <DraftBodyText>
                                    You are offline. Draft with serial number {draftSuccessSerialNumber} is stored on
                                    this device and will be created when you are back online.
                                </DraftBodyText>
                            ) : (
                                <DraftBodyText>
                                    Draft with serial number {draftSuccessSerialNumber} successfully created.
                                </DraftBodyText>
                            )}
                        </DraftBody>
                        <DraftFooter>
                            {draftError ? (
                                <>
                                    <DraftOutlinedButton onClick={handleCloseDraftSuccess}>Close</DraftOutlinedButton>
                                    <DraftPrimaryButton onClick={handleRetryDraft}>Try again</DraftPrimaryButton>
                                </>
                            ) : (
                                <>
                                    <DraftOutlinedButton variant="ghost" onClick={handleAddAnotherSensor}>
                                        Add another sensor
                                    </DraftOutlinedButton>
                                    {/* The draft only exists in the offline queue, so there is nothing to navigate to yet. */}
                                    {draftQueuedOffline ? (
                                        <DraftPrimaryButton onClick={handleCloseDraftSuccess}>Close</DraftPrimaryButton>
                                    ) : (
                                        <DraftPrimaryButton onClick={handleGoToDraft} disabled={isNavigatingToDraft}>
                                            {isNavigatingToDraft ? <Progress.Dots /> : 'Go to draft'}
                                        </DraftPrimaryButton>
                                    )}
                                </>
                            )}
                        </DraftFooter>
                    </DraftContent>
                </DraftDialog>
            )}

            {createdSensorSerialNumber && (isOnboardSuccess || isOnboardQueuedOffline) && (
                <DraftDialog open={!!createdSensorSerialNumber} onClose={handleCloseSensorSuccess}>
                    <DraftContent tabIndex={-1}>
                        <DraftHeader>
                            <DraftTitle>{isOnboardQueuedOffline ? 'Sensor saved offline' : 'Sensor added'}</DraftTitle>
                            <CloseButton
                                as="Link"
                                aria-label="Close dialog"
                                variant="ghost_icon"
                                onClick={handleCloseSensorSuccess}
                            >
                                <MobileLabel>Close</MobileLabel>
                                <StyledIcon data={close} size={24} />
                            </CloseButton>
                        </DraftHeader>
                        <DraftBody>
                            {isOnboardQueuedOffline ? (
                                <DraftBodyText>
                                    You are offline. Sensor {createdSensorSerialNumber} is stored on this device and
                                    will be added to the sensor overview when you are back online.
                                </DraftBodyText>
                            ) : (
                                <DraftBodyText>
                                    Sensor {createdSensorSerialNumber} has been added to sensor overview.
                                    <br />
                                    <br />
                                    This sensor must be activated before it is ready to use. To see how to activate this
                                    specific sensor, please check the Portfolio page.
                                </DraftBodyText>
                            )}
                        </DraftBody>
                        <DraftFooter>
                            <DraftOutlinedButton variant="ghost" onClick={handleAddAnotherSensorFromCreated}>
                                Add another sensor
                            </DraftOutlinedButton>
                            {/* The sensor only exists in the offline queue, so there is nothing to navigate to yet. */}
                            {isOnboardQueuedOffline ? (
                                <DraftPrimaryButton onClick={handleCloseSensorSuccess}>Close</DraftPrimaryButton>
                            ) : (
                                <DraftPrimaryButton
                                    onClick={handleGoToCreatedSensor}
                                    disabled={isNavigatingToCreatedSensor}
                                >
                                    {isNavigatingToCreatedSensor ? <Progress.Dots /> : 'Go to added sensor'}
                                </DraftPrimaryButton>
                            )}
                        </DraftFooter>
                    </DraftContent>
                </DraftDialog>
            )}
        </>
    )
}

const OnboardingStepperContainer = styled(Dialog)`
    display: flex;
    flex-direction: column;
    max-width: calc(100vw - 32px);
    width: 738px;
    height: fit-content;
    max-height: 120vh;
    padding: 8px 0 8px 0;
    gap: 8px;

    @media (max-width: 1024px) {
        position: fixed;
        left: 0;
        right: 0;
        bottom: calc(60px + env(safe-area-inset-bottom));
        top: auto;
        height: auto;
        width: 100%;
        max-width: 100%;
        margin: 0;
        background: #ffffff;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
        transform: none;
        border-radius: 0;
        max-height: calc(100vh - 64px - 60px - env(safe-area-inset-bottom));
        overflow-y: auto;
        overflow-x: hidden;
    }
`

const OverviewContainer = styled.div`
    padding: 16px;
`

const MenuHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 16px;
    flex-shrink: 0;

    button {
        background: none;
        border: none;
        cursor: pointer;
    }
    @media (max-width: 1024px) {
        color: #1d1d1d;
        margin-left: 0;
        margin-right: 0;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #dcdcdc;
    }
`

const StepContent = styled.div`
    margin: 0rem 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`

const CloseButton = styled(Button)`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    color: #007079;

    &:hover {
        background: #eaeaea;
    }

    @media (max-width: 1024px) {
        color: #1f6369;

        &:hover {
            background: #f5f5f5;
        }
    }
`
const StepperStyledButton = styled(Button)`
    ${baseLinkStyles}
    background: #007079;
    color: white;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    flex-wrap: wrap;
    display: inline-flex;
    line-height: 14px;
    white-space: normal;

    &:hover {
        background: #004f55;
        color: white;
    }

    @media (max-width: 1024px) {
        background: #007079;
        color: white;
        border-color: transparent;

        &hover {
            background: #007079;
        }
    }
`

const StepperStyledLinkButton = styled(Button)`
    ${baseLinkStyles}
    background: white;
    color: #007079;
    border-radius: 4px;
    border: 1px solid;
    border-color: #007079;
    padding: 8px 16px;
    font-size: 14px;

    &:hover {
        color: #004f55;
        background: #deedee;
        border: 1px solid;
        border-color: #007079;
    }

    @media (max-width: 1024px) {
        background: #ffffff;
        color: #1f6369;
        border-color: #21767e;

        &:hover {
            background: #f5f5f5;
            color: #1f6369;
            border-color: #21767e;
        }
    }
`

const StepperStyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;
    border-color: transparent;
    &:hover {
        color: white;
        background: #007079;
    }
`

const Label = styled.span`
    ${baseFont}
    @media (max-width: 1024px) {
        color: #1f6369;
        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`

const NavigateBackButton = styled(Button)`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    color: #007079;
    border-radius: 4px;

    &:hover {
        background: #eaeaea;
    }

    @media (max-width: 1024px) {
        color: #1f6369;
        padding: 8px 16px;

        &:hover {
            background: #f5f5f5;
        }
    }
`

const NavigateNextButton = styled(Button)`
    color: white;
    /* Keep border width reserved so EDS's hover-only border doesn't grow the button */
    border: 1px solid transparent;
    text-decoration: none;
    gap: 8px;
    padding: 8px 16px;
`

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0px;
    justify-content: space-between;
    flex-shrink: 0;
`

const RowContainerBottom = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    margin-bottom: 4px;
    margin-right: 8px;
    gap: 16px;
    flex-shrink: 0;
    @media (max-width: 1024px) {
        padding-top: 0px;
        gap: 8px;
    }
`

const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #007079;

    @media (max-width: 1024px) {
        color: #1f6369;
        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`

const MobileLabel = styled.span`
    display: none;

    @media (max-width: 1024px) {
        display: inline;
    }
`

const DraftDialog = styled(Dialog)`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(100% - 32px);
    max-width: 600px;
    background: white;
    border-radius: 8px;
    padding: 0;
    margin: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 10000;

    @media (max-width: 1024px) {
        position: fixed;
        left: 0;
        right: 0;
        bottom: calc(60px + env(safe-area-inset-bottom));
        top: auto;
        transform: none;
        width: 100%;
        max-width: 100%;
        background: #ffffff;
        border-radius: 0px;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
    }
`

const DraftContent = styled.div`
    display: flex;
    flex-direction: column;
    outline: none;
`

const DraftHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #dcdcdc;

    @media (max-width: 1024px) {
        margin-left: -16px;
        margin-right: -16px;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #dcdcdc;
    }
`

const DraftTitle = styled.h2`
    ${baseFont}
    font-size: 18px;
    font-weight: 400;
    margin: 0;
    color: black;

    @media (max-width: 1024px) {
        color: #1d1d1d;
        padding: 16px;
    }
`

const DraftBody = styled.div`
    padding: 0 24px 0 24px;
    flex: 1;
    @media (max-width: 1024px) {
        padding: 0 16px 0 16px;
    }
`

const DraftBodyText = styled.div`
    ${baseFont}
    font-size: 16px;
    line-height: 1.5;
    font-weight: 400;
    display: flex;
    flex-direction: column;
    padding: 12px 0;
    margin: 0;
    background: transparent;
    color: #333333;
    border-radius: 4px;

    @media (max-width: 1024px) {
        margin-top: 16px;
        margin-bottom: 8px;
        padding: 0;
        background: transparent;
        color: #1d1d1d;
    }
`

const DraftErrorMessage = styled.div`
    ${baseFont}
    font-size: 16px;
    line-height: 1.5;
    font-weight: 400;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
    margin: 0;
    background: transparent;
    color: #ff6666;
    border-radius: 4px;

    @media (max-width: 1024px) {
        margin-top: 16px;
        margin-bottom: 8px;
        padding: 0;
        background: transparent;
        color: #a50827;
    }
`

const DraftFooter = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    justify-content: flex-end;
    padding: 16px 24px;

    @media (max-width: 1024px) {
        padding: 16px;
        gap: 12px;
    }
`

const DraftOutlinedButton = styled(Button)`
    ${baseFont}
    margin-right: auto;
    background: white;
    color: #007079;
    border-radius: 4px;
    border: 1px solid #007079;
    font-size: 14px;
    padding: 8px 16px;

    @media (max-width: 1024px) {
        background: #ffffff;
        color: #1f6369;
        border: 1px solid #21767e;

        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`

const DraftPrimaryButton = styled(Button)`
    ${baseFont}
    background: #007079;
    color: white;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;

    @media (max-width: 1024px) {
        background: #007079;
        color: white;

        &:hover {
            background: #007079;
            color: white;
        }
    }
`
export default OnboardingStepper
